import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, Building2, Smartphone, DollarSign, 
  Shield, CheckCircle, Clock, FileText, ArrowRight, Lock, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const paymentMethods = [
  { id: 'credit-card', icon: CreditCard, title: 'Credit/Debit Cards', titleEs: 'Tarjetas de Crédito/Débito', description: 'Visa, Mastercard, American Express, Discover', descriptionEs: 'Visa, Mastercard, American Express, Discover' },
  { id: 'paypal', icon: DollarSign, title: 'PayPal', titleEs: 'PayPal', description: 'Pay securely with your PayPal account', descriptionEs: 'Pague de forma segura con su cuenta de PayPal' },
  { id: 'zelle', icon: Smartphone, title: 'Zelle', titleEs: 'Zelle', description: 'Fast bank-to-bank transfers', descriptionEs: 'Transferencias rápidas de banco a banco' },
  { id: 'bank-transfer', icon: Building2, title: 'Bank Transfer (ACH/Wire)', titleEs: 'Transferencia Bancaria (ACH/Wire)', description: 'Direct bank transfer via Chase', descriptionEs: 'Transferencia bancaria directa vía Chase' },
  { id: 'affirm', icon: Clock, title: 'Affirm - Pay Over Time', titleEs: 'Affirm - Pague a Plazos', description: 'Split your payment into 4 interest-free installments', descriptionEs: 'Divida su pago en 4 cuotas sin intereses' }
];

const paymentPolicies = [
  { text: 'All prices are quoted in US Dollars (USD)', textEs: 'Todos los precios están en Dólares Estadounidenses (USD)' },
  { text: '50% deposit required to initiate project work', textEs: 'Se requiere un depósito del 50% para iniciar el trabajo' },
  { text: 'Remaining 50% due upon project completion and client approval', textEs: 'El 50% restante se debe al completar el proyecto' },
  { text: 'For projects over $10,000, payment plans are available', textEs: 'Para proyectos mayores a $10,000, hay planes de pago' },
  { text: 'Credit card payments may include a 3% processing fee', textEs: 'Pagos con tarjeta pueden incluir tarifa del 3%' },
  { text: 'Bank transfers and Zelle payments have no additional fees', textEs: 'Transferencias bancarias y Zelle sin tarifas adicionales' },
  { text: 'Affirm financing subject to credit approval', textEs: 'Financiamiento Affirm sujeto a aprobación de crédito' },
  { text: 'Refunds are processed within 5-7 business days', textEs: 'Reembolsos en 5-7 días hábiles' },
  { text: 'Cancellation fees may apply for work already completed', textEs: 'Pueden aplicar tarifas de cancelación' }
];

const customerProtection = [
  { text: 'SSL encrypted transactions', textEs: 'Transacciones encriptadas con SSL' },
  { text: 'PCI DSS compliant', textEs: 'Compatible con PCI DSS' },
  { text: 'No storage of credit cards', textEs: 'No almacenamos tarjetas' },
  { text: 'Detailed invoices', textEs: 'Facturas detalladas' },
  { text: 'Written estimates', textEs: 'Estimados por escrito' }
];

export default function Payments() {
  const { language } = useLanguage();
  const isEs = language === 'es';

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero - EXACT Abacus colors */}
      <section className="py-12" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 51, 102, 0.867) 50%, rgba(0, 166, 81, 0.25) 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              {isEs ? 'Métodos de Pago' : 'Payment Methods'}
            </h1>
            <p className="text-white/80 text-sm">
              {isEs ? 'Opciones de pago seguras y flexibles para sus proyectos' : 'Secure and flexible payment options for your projects'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Accepted Payment Logos Section */}
      <section className="py-8 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <h2 className="text-sm font-medium text-center mb-6 text-gray-600 uppercase tracking-wider">
            {isEs ? 'Aceptamos' : 'We Accept'}
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-4 max-w-3xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2 shadow-sm">
              <span className="text-xl font-bold text-blue-700">VISA</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2 shadow-sm">
              <div className="flex">
                <div className="w-5 h-5 bg-red-500 rounded-full -mr-2"></div>
                <div className="w-5 h-5 bg-yellow-500 rounded-full opacity-80"></div>
              </div>
              <span className="text-sm font-bold text-gray-700">Mastercard</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
              <span className="text-sm font-bold text-blue-600">AMEX</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
              <span className="text-sm font-bold text-orange-500">Discover</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
              <span className="text-sm font-bold text-blue-800">PayPal</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
              <span className="text-sm font-bold text-purple-600">Zelle</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
              <span className="text-sm font-bold text-gray-700">Affirm</span>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Methods - WHITE */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-bold text-center mb-8" style={{ color: 'rgb(0, 51, 102)' }}>
            {isEs ? 'Métodos de Pago Aceptados' : 'Accepted Payment Methods'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {paymentMethods.map((method, index) => (
              <motion.div key={method.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }}>
                <Card className="bg-white border border-gray-200 hover:border-[#00A651] hover:shadow-lg transition-all h-full">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 166, 81, 0.082)' }}>
                        <method.icon className="w-5 h-5" style={{ color: 'rgb(0, 166, 81)' }} />
                      </div>
                      <h3 className="font-semibold text-gray-800">{isEs ? method.titleEs : method.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm">{isEs ? method.descriptionEs : method.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Information - Light gray */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Zelle */}
            <Card className="bg-white border border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Smartphone className="w-5 h-5 text-purple-600" />
                  {isEs ? 'Información Zelle' : 'Zelle Information'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">{isEs ? 'Enviar pago a:' : 'Send payment to:'}</span>
                  <span className="font-medium text-gray-800">iqesllc@gmail.com</span>
                </div>
                <p className="text-gray-500 text-sm mt-2">{isEs ? 'Use este email en su aplicación bancaria.' : 'Use this email in your banking app.'}</p>
              </CardContent>
            </Card>

            {/* Bank */}
            <Card className="bg-white border border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  {isEs ? 'Transferencia Bancaria' : 'Bank Transfer'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-500">Bank:</span><span className="font-medium">Chase Bank</span></div>
                  <div className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-500">Account:</span><span className="font-medium">IQES LLC</span></div>
                  <div className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-500">Account #:</span><span className="font-medium">****0033</span></div>
                  <div className="flex justify-between py-1"><span className="text-gray-500">Routing:</span><span className="font-medium">267084131</span></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Cards */}
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto mt-6">
            <Card className="bg-orange-50 border border-orange-200">
              <CardContent className="p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">{isEs ? 'Depósito Requerido' : 'Deposit Required'}</h4>
                  <p className="text-sm text-gray-600">{isEs ? '50% para iniciar, 50% al completar' : '50% to start, 50% upon completion'}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border border-green-200">
              <CardContent className="p-4 flex items-start gap-3">
                <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">{isEs ? 'Pagos Seguros' : 'Secure Payments'}</h4>
                  <p className="text-sm text-gray-600">{isEs ? 'Todas las transacciones encriptadas' : 'All transactions encrypted'}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Policies - WHITE */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <Card className="bg-white border border-gray-200 shadow-sm max-w-3xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-teal-600" />
                {isEs ? 'Políticas y Términos' : 'Policies & Terms'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {paymentPolicies.map((policy, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{isEs ? policy.textEs : policy.text}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Customer Protection - Navy Blue */}
      <section className="py-12" style={{ backgroundColor: '#003366' }}>
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-bold text-white text-center mb-8">{isEs ? 'Protección al Cliente' : 'Customer Protection'}</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {customerProtection.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm text-white/90">{isEs ? item.textEs : item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Notice - WHITE */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <Card className="bg-yellow-50 border border-yellow-200 max-w-3xl mx-auto">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-800 text-sm mb-1">{isEs ? 'Aviso Legal' : 'Legal Notice'}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {isEs 
                    ? 'Al realizar un pago, acepta nuestros términos. IQES LLC se reserva el derecho de modificar precios. Todo trabajo está garantizado. Para disputas, contáctenos antes de chargebacks.'
                    : 'By making a payment, you agree to our terms. IQES LLC reserves the right to modify pricing. All work is guaranteed. For disputes, contact us before chargebacks.'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA - Navy Blue */}
      <section className="py-10" style={{ backgroundColor: '#003366' }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-2">{isEs ? '¿Listo para comenzar?' : 'Ready to start?'}</h2>
          <p className="text-white/80 text-sm mb-4">{isEs ? 'Contáctenos para un presupuesto' : 'Contact us for a quote'}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/quote"><Button className="text-white" style={{ backgroundColor: '#00A651' }}>{isEs ? 'Cotización' : 'Get Quote'}<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/contact"><Button variant="outline" className="border-white text-white hover:bg-white/10">{isEs ? 'Contactar' : 'Contact Us'}</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
